import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Container, 
  Grid, 
  Paper, 
  CircularProgress, 
  Card, 
  CardMedia, 
  CardContent,
  Alert,
  Divider,
  useTheme,
  alpha,
  Chip,
  IconButton,
  Tooltip
} from '@mui/material'
import { 
  Add as AddIcon,
  FolderSpecial as FolderIcon,
  AccessTime as TimeIcon,
  Info as InfoIcon,
  BrushOutlined as BrushIcon,
  CheckroomOutlined as FashionIcon,
  SearchOutlined
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { ProjectProvider, useProjects } from '../contexts/ProjectContext'
import AppHeader from '../components/common/AppHeader'
import { normalizeProjectName } from '../utils/helpers'

const ProjectGrid = () => {
  const { projects, loading, error, setSelectedProject, createProject, loadProjects } = useProjects()
  const [newProjectName, setNewProjectName] = useState('')
  const [normalizedName, setNormalizedName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const navigate = useNavigate()
  const theme = useTheme()
  const { mode } = useParams<{ mode: string }>()

  // The loadProjects function is already called in the ProjectContext's useEffect
  // We don't need to call it again here to avoid infinite loops

  const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setNewProjectName(value)
    setNormalizedName(normalizeProjectName(value))
  }

  const handleCreateProject = async () => {
    if (!normalizedName) {
      setCreateError('Wprowadź poprawną nazwę projektu')
      return
    }

    setIsCreating(true)
    setCreateError(null)

    try {
      const success = await createProject(normalizedName)
      if (success) {
        setNewProjectName('')
        setNormalizedName('')
        await loadProjects()
      } else {
        setCreateError('Nie udało się utworzyć projektu. Spróbuj ponownie.')
      }
    } catch (error) {
      console.error('Error creating project:', error)
      setCreateError('Wystąpił błąd podczas tworzenia projektu.')
    } finally {
      setIsCreating(false)
    }
  }

  const handleSelectProject = (project: any) => {
    // Store the selected mode with the project
    project.mode = mode || 'on-model';
    setSelectedProject(project)
    
    // Pass the mode as a query parameter
    navigate(`/designer/${project.name}?mode=${mode || 'on-model'}`)
  }

  // Filter projects based on search query
  const filteredProjects = searchQuery 
    ? projects.filter(project => 
        project.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : projects;

  if (loading && projects.length === 0) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          flexDirection: 'column',
          height: '50vh'
        }}
      >
        <CircularProgress size={60} thickness={3} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
          Ładowanie projektów...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ py: 6 }}>
      {/* Hero section */}
      <Box 
        sx={{ 
          backgroundColor: alpha(theme.palette.primary.main, 0.05),
          borderRadius: 4,
          p: { xs: 3, md: 5 },
          mb: 6,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box 
          sx={{ 
            position: 'absolute',
            top: -80,
            right: -80,
            width: 200,
            height: 200,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            zIndex: 0
          }}
        />
        <Box 
          sx={{ 
            position: 'absolute',
            bottom: -60,
            left: -60,
            width: 150,
            height: 150,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.secondary.main, 0.08),
            zIndex: 0
          }}
        />
        
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <FashionIcon sx={{ mr: 1, fontSize: 28, color: theme.palette.primary.main }} />
            <Typography 
              variant="h4" 
              component="h1"
              sx={{ 
                fontWeight: 600,
                color: theme.palette.primary.main
              }}
            >
              Projekty Fashion Babe
            </Typography>
          </Box>
          
          <Typography variant="h6" sx={{ mb: 4, color: 'text.secondary', fontWeight: 400 }}>
            {mode === 'on-model' 
              ? 'Zarządzaj projektami dla trybu Zdjęcia na modelu' 
              : mode === 'flat-lay'
                ? 'Zarządzaj projektami dla trybu Wirtualne modele'
                : 'Zarządzaj swoimi projektami wirtualnej przymierzalni'
            }
          </Typography>
        
          {/* Sekcja tworzenia nowego projektu */}
          <Paper 
            elevation={2}
            sx={{ 
              p: 3, 
              borderRadius: 3,
              backgroundColor: 'white',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06)'
            }}
          >
            <Typography variant="h6" gutterBottom>
              <Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>
                <AddIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                Stwórz nowy projekt
              </Box>
            </Typography>
            
            <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
              Nazwij swój projekt i rozpocznij tworzenie wirtualnych stylizacji
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                label="Nazwa projektu"
                variant="outlined"
                placeholder="Przykład: Kolekcja Wiosna 2025"
                value={newProjectName}
                onChange={handleProjectNameChange}
                sx={{ 
                  flexGrow: 1, 
                  maxWidth: { xs: '100%', sm: 400 },
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.8)
                  }
                }}
                error={!!createError}
                helperText={createError || (normalizedName ? `Nazwa katalogu: ${normalizedName}` : '')}
              />
              
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreateProject}
                disabled={!normalizedName || isCreating}
                sx={{ 
                  height: 56,
                  px: 3,
                  boxShadow: '0 4px 12px rgba(74, 105, 187, 0.2)',
                }}
              >
                {isCreating ? <CircularProgress size={24} /> : 'Stwórz projekt'}
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* Lista istniejących projektów */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography 
          variant="h5" 
          sx={{ 
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center', 
          }}
        >
          <FolderIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
          Twoje projekty
        </Typography>
        
        <TextField
          placeholder="Wyszukaj projekt..."
          variant="outlined"
          size="small"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchOutlined sx={{ mr: 1, color: 'text.secondary' }} />,
          }}
          sx={{ 
            width: { xs: '100%', sm: 250 },
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
            }
          }}
        />
      </Box>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
          }}
        >
          {error}
        </Alert>
      )}
      
      {projects.length === 0 ? (
        <Paper 
          sx={{ 
            p: 4, 
            textAlign: 'center', 
            borderRadius: 3,
            backgroundColor: alpha(theme.palette.background.paper, 0.8),
            boxShadow: '0 6px 16px rgba(0, 0, 0, 0.05)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}
        >
          <BrushIcon sx={{ fontSize: 48, color: alpha(theme.palette.primary.main, 0.7) }} />
          <Typography variant="h6" color="text.secondary">
            Nie masz jeszcze żadnych projektów
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 500, mb: 2 }}>
            Stwórz swój pierwszy projekt, aby rozpocząć projektowanie wirtualnych stylizacji
          </Typography>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => document.getElementById('new-project-name')?.focus()}
          >
            Stwórz pierwszy projekt
          </Button>
        </Paper>
      ) : (
        <>
          {filteredProjects.length === 0 ? (
            <Paper 
              sx={{ 
                p: 4, 
                textAlign: 'center', 
                borderRadius: 3,
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.05)',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Nie znaleziono projektów pasujących do "{searchQuery}"
              </Typography>
              <Button
                variant="text"
                color="primary"
                onClick={() => setSearchQuery('')}
                sx={{ mt: 2 }}
              >
                Wyczyść wyszukiwanie
              </Button>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredProjects.map((project) => (
                <Grid item xs={12} sm={6} md={4} key={project.name}>
                  <Card 
                    sx={{ 
                      cursor: 'pointer',
                      borderRadius: 3,
                      overflow: 'hidden',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 32px rgba(0, 0, 0, 0.12)',
                      },
                      position: 'relative'
                    }}
                    onClick={() => handleSelectProject(project)}
                  >
                    <Box sx={{ position: 'relative' }}>
                      <CardMedia
                        component="img"
                        height="200"
                        image={project.thumbnail || '/images-for-project/default-project.jpg'}
                        alt={project.name}
                        sx={{ 
                          objectFit: 'cover',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: 'linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.6) 100%)',
                        }}
                      />
                      <Chip
                        label="Fashion Babe"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          left: 12,
                          backgroundColor: alpha(theme.palette.primary.main, 0.9),
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                      <Tooltip title="Informacje o projekcie">
                        <IconButton
                          size="small"
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            backgroundColor: alpha('#fff', 0.9),
                            '&:hover': {
                              backgroundColor: '#fff',
                            },
                            width: 30,
                            height: 30,
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            // Show project info dialog
                            console.log('Info clicked for project:', project.name);
                          }}
                        >
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    
                    <CardContent sx={{ flexGrow: 1, pb: 2 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontWeight: 600,
                          mb: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {project.name}
                      </Typography>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <TimeIcon sx={{ color: 'text.secondary', fontSize: '0.9rem', mr: 0.5 }} />
                        <Typography variant="body2" color="text.secondary">
                          Ostatnio edytowany: {project.modified_date || new Date().toLocaleDateString()}
                        </Typography>
                      </Box>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Chip 
                          label="8 stylizacji" 
                          size="small"
                          sx={{ 
                            backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                            color: theme.palette.secondary.main,
                            fontWeight: 500
                          }}
                        />
                        <Button 
                          size="small" 
                          variant="text"
                          sx={{ fontSize: '0.8rem' }}
                        >
                          Otwórz
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}
    </Box>
  )
}

const ProjectSelection = () => {
  const { user, logout, isAdmin } = useAuth()
  const navigate = useNavigate()
  const { mode } = useParams<{ mode: string }>()
  
  // If there's no mode, redirect to mode selection
  useEffect(() => {
    if (!mode) {
      navigate('/modes');
    }
  }, [mode, navigate]);
  
  // Determine title based on selected mode
  const getTitle = () => {
    if (mode === 'on-model') {
      return "Wybór projektu - Zdjęcia na modelu";
    } else if (mode === 'flat-lay') {
      return "Wybór projektu - Wirtualne modele";
    }
    return "Wybór projektu";
  };

  // Go back to mode selection
  const handleBack = () => {
    navigate('/modes');
  };

  return (
    <ProjectProvider>
      <AppHeader 
        title={getTitle()}
        username={user?.username} 
        showAdminButton={isAdmin}
        showBackButton={true}
        onBackClick={handleBack}
        onLogout={logout}
      />
      <Container maxWidth="lg">
        <ProjectGrid />
      </Container>
    </ProjectProvider>
  )
}

export default ProjectSelection
